import type { PermissionScopeType } from '@repo/domain';

import type { RolePermission, RolePermissions } from '@features/auth/types';

interface RoleDefinition {
  scope: string;
}

interface PermissionDomainDefinition {
  grants: Record<string, string[]>;
}

const PERMISSION_SCOPE_TYPES: readonly string[] = ['system', 'organization', 'parking'];

const isPermissionScopeType = (value: string): value is PermissionScopeType => PERMISSION_SCOPE_TYPES.includes(value);

/**
 * Turns the contract's role dictionary and its permission domains into one lookup of what each role
 * carries. A role naming a scope this app does not know is left out, so it can never grant anything.
 */
export function buildRolePermissions(
  roles: Record<string, RoleDefinition>,
  domains: PermissionDomainDefinition[]
): RolePermissions {
  const entries = Object.entries(roles).flatMap(([roleKey, role]): Array<[string, RolePermission]> => {
    if (!isPermissionScopeType(role.scope)) return [];

    const permissionKeys = new Set(domains.flatMap((domain) => domain.grants[roleKey] ?? []));

    return [[roleKey, { scopeType: role.scope, permissionKeys }]];
  });

  return new Map(entries);
}
